import React, { useState, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PENDING = 'Pendiente de conexión con Supabase';

const ACCENT = '#D6EF4E';
const ACCENT_DIM = 'rgba(214,239,78,0.08)';
const ACCENT_BORDER = 'rgba(214,239,78,0.20)';

// ─── KPI DATA ─────────────────────────────────────────────────────────────────
const kpiRows = [
  [
    { id: 'fac_hoy', title: 'Facturación Hoy', icon: 'Zap', color: '#D6EF4E', unit: '€', trend: null },
    { id: 'fac_mes', title: 'Facturación Mes', icon: 'TrendingUp', color: '#34D399', unit: '€', trend: null },
    { id: 'fac_anio', title: 'Facturación Año', icon: 'BarChart2', color: '#60A5FA', unit: '€', trend: null },
    { id: 'ingresos_cobrados', title: 'Ingresos Cobrados', icon: 'CheckCircle', color: '#A78BFA', unit: '€', trend: null },
    { id: 'beneficio_bruto', title: 'Beneficio Bruto', icon: 'TrendingUp', color: '#34D399', unit: '€', trend: null },
  ],
  [
    { id: 'beneficio_op', title: 'Beneficio Operativo', icon: 'Activity', color: '#FBBF24', unit: '€', trend: null },
    { id: 'beneficio_neto', title: 'Beneficio Neto Est.', icon: 'DollarSign', color: '#34D399', unit: '€', trend: null },
    { id: 'margen_global', title: 'Margen Global', icon: 'Percent', color: '#D6EF4E', unit: '%', trend: null },
    { id: 'tesoreria', title: 'Tesorería Disponible', icon: 'Wallet', color: '#60A5FA', unit: '€', trend: null },
    { id: 'saldo_bancario', title: 'Saldo Bancario', icon: 'Landmark', color: '#A78BFA', unit: '€', trend: null },
  ],
  [
    { id: 'caja', title: 'Caja Disponible', icon: 'Banknote', color: '#FBBF24', unit: '€', trend: null },
    { id: 'stock_valorizado', title: 'Stock Valorizado', icon: 'Package', color: '#F97316', unit: '€', trend: null },
    { id: 'cobros_pendientes', title: 'Cobros Pendientes', icon: 'ArrowDownCircle', color: '#FBBF24', unit: '€', trend: null },
    { id: 'pagos_pendientes', title: 'Pagos Pendientes', icon: 'ArrowUpCircle', color: '#F87171', unit: '€', trend: null },
    { id: 'cashflow_30', title: 'Cashflow 30 días', icon: 'TrendingUp', color: '#34D399', unit: '€', trend: null },
  ],
  [
    { id: 'punto_equilibrio', title: 'Punto de Equilibrio', icon: 'Target', color: '#60A5FA', unit: '€', trend: null },
    { id: 'venta_diaria', title: 'Venta Diaria Necesaria', icon: 'Calendar', color: '#D6EF4E', unit: '€', trend: null },
    { id: 'capital_circulante', title: 'Capital Circulante', icon: 'RefreshCw', color: '#A78BFA', unit: '€', trend: null },
    { id: 'deuda_financiera', title: 'Deuda Financiera', icon: 'CreditCard', color: '#F87171', unit: '€', trend: null },
    { id: 'credito_disponible', title: 'Crédito Disponible', icon: 'Shield', color: '#34D399', unit: '€', trend: null },
  ],
];

// ─── DEPARTMENTS ──────────────────────────────────────────────────────────────
const departments = [
  { id: 'sala', name: 'Sala de Corte', icon: '🔪', color: '#D6EF4E', path: '/dashboard-sala-corte' },
  { id: 'distribucion', name: 'Distribución', icon: '🚚', color: '#60A5FA', path: '/distribucion' },
  { id: 'tienda', name: 'Tienda General', icon: '🏪', color: '#34D399', path: '/tienda-general' },
  { id: 'corporativo', name: 'Corporativo', icon: '🏢', color: '#A78BFA', path: '/finanzas-corporativas' },
];

// ─── ALERTS ───────────────────────────────────────────────────────────────────
const alertTypes = [
  { id: 'critica', label: 'Crítica', color: '#F87171', bg: 'rgba(248,113,113,0.10)', border: 'rgba(248,113,113,0.25)' },
  { id: 'alta', label: 'Alta', color: '#FBBF24', bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.25)' },
  { id: 'media', label: 'Media', color: '#60A5FA', bg: 'rgba(96,165,250,0.10)', border: 'rgba(96,165,250,0.25)' },
  { id: 'info', label: 'Informativa', color: '#A78BFA', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.25)' },
];

const sampleAlerts = [
  { id: 1, type: 'critica', title: 'Facturas vencidas sin cobrar', desc: PENDING, module: '/fin-facturacion' },
  { id: 2, type: 'alta', title: 'Productos bajo stock mínimo', desc: PENDING, module: '/dist-inventario' },
  { id: 3, type: 'alta', title: 'Pagos próximos sin saldo suficiente', desc: PENDING, module: '/fin-tesoreria' },
  { id: 4, type: 'media', title: 'Clientes sin actividad reciente', desc: PENDING, module: '/dist-clientes' },
  { id: 5, type: 'media', title: 'Pólizas próximas a vencer', desc: PENDING, module: '/fin-bancos' },
  { id: 6, type: 'info', title: 'Movimientos bancarios sin conciliar', desc: PENDING, module: '/fin-bancos' },
  { id: 7, type: 'info', title: 'Facturas pendientes de OCR', desc: PENDING, module: '/fin-facturacion' },
];

const aiSuggestions = [
  { icon: 'AlertTriangle', color: '#F87171', text: 'Riesgo de rotura de stock en productos clave' },
  { icon: 'TrendingDown', color: '#FBBF24', text: 'Caída de margen detectada en Distribución' },
  { icon: 'Clock', color: '#60A5FA', text: 'Facturas vencidas pendientes de revisión' },
  { icon: 'UserX', color: '#A78BFA', text: 'Clientes que han dejado de comprar este mes' },
  { icon: 'ShoppingCart', color: '#34D399', text: 'Necesidad de reposición en inventario Tienda' },
  { icon: 'Wallet', color: '#D6EF4E', text: 'Previsión de tesorería a 30 días disponible' },
  { icon: 'Target', color: '#F97316', text: 'Objetivo de venta diario pendiente de calcular' },
  { icon: 'BarChart2', color: '#34D399', text: 'Desviaciones presupuestarias detectadas' },
];

const aiQuestions = [
  '¿Cuánto necesito vender hoy para cubrir gastos?',
  '¿Qué departamento genera más beneficio?',
  '¿Qué clientes tienen facturas vencidas?',
  '¿Qué productos debo reponer?',
  '¿Cuál es el margen real de Distribución?',
  '¿Cuál será mi tesorería dentro de 30 días?',
  '¿Qué producto tiene peor rotación?',
];

const DATE_FILTERS = ['Hoy', 'Ayer', 'Esta semana', 'Semana anterior', 'Este mes', 'Mes anterior', 'Trimestre', 'Año', 'Ejercicio fiscal', 'Personalizado'];

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle, accent = ACCENT }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
      <Icon name={icon} size={18} style={{ color: accent }} />
    </div>
    <div>
      <h2 className="erp-section-title">{title}</h2>
      {subtitle && <p className="erp-secondary-text mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex-1 h-px ml-3" style={{ background: 'rgba(255,255,255,0.06)' }} />
  </div>
);

const PendingBadge = () => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>
    <span className="w-1.5 h-1.5 rounded-full bg-white/20 inline-block" />
    Pendiente de conexión
  </span>
);

const KPICard = ({ kpi, onClick }) => (
  <button
    onClick={() => onClick?.(kpi)}
    className="relative rounded-2xl p-4 flex flex-col gap-2 text-left w-full transition-all duration-200 hover:scale-[1.02] hover:shadow-xl group"
    style={{
      background: `linear-gradient(135deg, ${kpi?.color}08 0%, rgba(17,17,17,0.97) 100%)`,
      border: `1px solid ${kpi?.color}22`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
    }}>
    <div className="flex items-start justify-between gap-2">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${kpi?.color}12`, border: `1px solid ${kpi?.color}25` }}>
        <Icon name={kpi?.icon} size={17} style={{ color: kpi?.color }} />
      </div>
      <PendingBadge />
    </div>
    <div>
      <div className="erp-kpi-value" style={{ color: kpi?.color }}>—</div>
      <p className="erp-kpi-label mt-1">{kpi?.title}</p>
    </div>
    <div className="flex items-center gap-1.5 pt-2 border-t border-white/5">
      <Icon name="Info" size={11} className="text-white/20" />
      <span className="text-xs text-white/25">Pulsa para ampliar</span>
    </div>
    <span className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
      style={{ boxShadow: `0 0 0 1px ${kpi?.color}35, 0 8px 32px ${kpi?.color}12` }} />
  </button>
);

const DeptCard = ({ dept, navigate }) => (
  <div className="rounded-2xl p-5 flex flex-col gap-4 transition-all duration-200 hover:scale-[1.01]"
    style={{
      background: `linear-gradient(135deg, ${dept?.color}06 0%, rgba(17,17,17,0.97) 100%)`,
      border: `1px solid ${dept?.color}22`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{dept?.icon}</span>
        <div>
          <h3 className="erp-section-title text-base">{dept?.name}</h3>
          <PendingBadge />
        </div>
      </div>
      <button onClick={() => navigate(dept?.path)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
        style={{ background: `${dept?.color}15`, color: dept?.color, border: `1px solid ${dept?.color}25` }}>
        <Icon name="ExternalLink" size={12} />
        Ver módulo
      </button>
    </div>
    <div className="h-px w-full" style={{ background: `linear-gradient(90deg, ${dept?.color}30 0%, transparent 100%)` }} />
    <div className="grid grid-cols-2 gap-3">
      {['Facturación', 'Costes', 'Beneficio', 'Margen %']?.map((label) => (
        <div key={label} className="flex flex-col gap-1">
          <p className="erp-secondary-text uppercase tracking-widest" style={{ fontSize: '0.6875rem' }}>{label}</p>
          <div className="h-7 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between pt-1">
      <span className="erp-secondary-text">Objetivo cumplimiento</span>
      <div className="flex-1 mx-3 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-full rounded-full w-0" style={{ background: dept?.color }} />
      </div>
      <span className="erp-secondary-text">—%</span>
    </div>
  </div>
);

const AlertCard = ({ alert, onAction }) => {
  const type = alertTypes?.find(t => t?.id === alert?.type) || alertTypes?.[3];
  return (
    <div className="rounded-xl p-4 flex flex-col gap-2 transition-all hover:scale-[1.01]"
      style={{ background: type?.bg, border: `1px solid ${type?.border}` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ background: type?.color }} />
          <p className="erp-table-body font-semibold text-white leading-snug">{alert?.title}</p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ background: `${type?.color}20`, color: type?.color, border: `1px solid ${type?.color}30` }}>
          {type?.label}
        </span>
      </div>
      <p className="erp-secondary-text pl-4">{alert?.desc}</p>
      <div className="flex items-center gap-2 pl-4 pt-1 flex-wrap">
        {['Revisar', 'Asignar', 'Posponer', 'Resolver']?.map(action => (
          <button key={action} onClick={() => onAction?.(alert, action)}
            className="text-xs px-2.5 py-1 rounded-lg transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.10)' }}>
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

const TreasuryBlock = () => {
  const items = [
    { label: 'Saldo Bancario', icon: 'Landmark', color: '#60A5FA' },
    { label: 'Caja', icon: 'Banknote', color: '#D6EF4E' },
    { label: 'Cobros Previstos', icon: 'ArrowDownCircle', color: '#34D399' },
    { label: 'Pagos Previstos', icon: 'ArrowUpCircle', color: '#F87171' },
    { label: 'Posición Neta', icon: 'Scale', color: '#A78BFA' },
    { label: 'Liquidez Inmediata', icon: 'Droplets', color: '#FBBF24' },
  ];
  const forecasts = ['7 días', '30 días', '60 días', '90 días'];
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.15)' }}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {items?.map(item => (
          <div key={item?.label} className="rounded-xl p-3 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-2">
              <Icon name={item?.icon} size={14} style={{ color: item?.color }} />
              <span className="erp-secondary-text" style={{ fontSize: '0.6875rem' }}>{item?.label}</span>
            </div>
            <div className="erp-kpi-value text-xl" style={{ color: item?.color }}>—</div>
            <PendingBadge />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {forecasts?.map(f => (
          <div key={f} className="rounded-xl p-3 text-center"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="erp-secondary-text mb-1">Previsión {f}</p>
            <div className="erp-kpi-value text-xl text-white/40">—</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InventoryBlock = () => {
  const items = [
    { label: 'Stock Tienda', color: '#34D399', icon: 'Store' },
    { label: 'Stock Distribución', color: '#60A5FA', icon: 'Truck' },
    { label: 'Stock Total', color: '#D6EF4E', icon: 'Package' },
    { label: 'Valor a Coste', color: '#FBBF24', icon: 'DollarSign' },
    { label: 'Valor a PVP', color: '#A78BFA', icon: 'Tag' },
    { label: 'Beneficio Potencial', color: '#34D399', icon: 'TrendingUp' },
    { label: 'Agotados', color: '#F87171', icon: 'AlertCircle' },
    { label: 'Bajo Mínimo', color: '#FBBF24', icon: 'AlertTriangle' },
  ];
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(52,211,153,0.04)', border: '1px solid rgba(52,211,153,0.15)' }}>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        {items?.map(item => (
          <div key={item?.label} className="rounded-xl p-3 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <Icon name={item?.icon} size={14} style={{ color: item?.color }} />
            <div className="erp-kpi-value text-xl" style={{ color: item?.color }}>—</div>
            <p className="erp-secondary-text" style={{ fontSize: '0.6875rem' }}>{item?.label}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-3 rounded-xl"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <Icon name="Info" size={14} className="text-white/30 flex-shrink-0" />
        <p className="erp-secondary-text">{PENDING}</p>
      </div>
    </div>
  );
};

const CommercialBlock = () => {
  const items = [
    { label: 'Clientes Activos', icon: 'Users', color: '#34D399' },
    { label: 'Clientes Nuevos', icon: 'UserPlus', color: '#D6EF4E' },
    { label: 'Clientes Perdidos', icon: 'UserMinus', color: '#F87171' },
    { label: 'Oportunidades', icon: 'Target', color: '#A78BFA' },
    { label: 'Pedidos Pendientes', icon: 'ShoppingCart', color: '#FBBF24' },
    { label: 'Comisiones', icon: 'Award', color: '#60A5FA' },
  ];
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.15)' }}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {items?.map(item => (
          <div key={item?.label} className="rounded-xl p-3 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <Icon name={item?.icon} size={14} style={{ color: item?.color }} />
            <div className="erp-kpi-value text-xl" style={{ color: item?.color }}>—</div>
            <p className="erp-secondary-text" style={{ fontSize: '0.6875rem' }}>{item?.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SalaCorteBlock = () => {
  const items = [
    { label: 'Piezas Procesadas', icon: 'Scissors', color: '#D6EF4E' },
    { label: 'Kg Procesados', icon: 'Weight', color: '#FBBF24' },
    { label: 'Rendimiento Medio', icon: 'Activity', color: '#34D399' },
    { label: 'Merma Media', icon: 'TrendingDown', color: '#F87171' },
    { label: 'Coste Sala', icon: 'DollarSign', color: '#60A5FA' },
    { label: 'Facturación Sala', icon: 'TrendingUp', color: '#A78BFA' },
    { label: 'Beneficio Sala', icon: 'BarChart2', color: '#34D399' },
    { label: 'Clientes Activos', icon: 'Users', color: '#D6EF4E' },
  ];
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(214,239,78,0.04)', border: '1px solid rgba(214,239,78,0.15)' }}>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {items?.map(item => (
          <div key={item?.label} className="rounded-xl p-3 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <Icon name={item?.icon} size={14} style={{ color: item?.color }} />
            <div className="erp-kpi-value text-xl" style={{ color: item?.color }}>—</div>
            <p className="erp-secondary-text" style={{ fontSize: '0.6875rem' }}>{item?.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const OperativeTable = ({ title, icon, color, columns, rows }) => (
  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
    <div className="flex items-center justify-between px-4 py-3"
      style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center gap-2">
        <Icon name={icon} size={15} style={{ color }} />
        <span className="erp-section-title text-sm">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <button className="text-xs px-2.5 py-1 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
          Exportar
        </button>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {columns?.map(col => (
              <th key={col} className="erp-table-header px-4 py-2.5 text-left whitespace-nowrap">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns?.length} className="px-4 py-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <Icon name="Database" size={22} className="text-white/15" />
                <p className="erp-secondary-text">{PENDING}</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

const FilterBar = ({ activeFilter, setActiveFilter, showAdvanced, setShowAdvanced }) => (
  <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
    {/* Date filters */}
    <div className="flex items-center gap-2 flex-wrap mb-3">
      <span className="erp-filter-label mr-1 flex-shrink-0">Periodo:</span>
      {DATE_FILTERS?.map(f => (
        <button key={f} onClick={() => setActiveFilter(f)}
          className="px-3 py-1.5 rounded-lg text-sm transition-all duration-150 flex-shrink-0"
          style={activeFilter === f
            ? { background: ACCENT_DIM, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }
            : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {f}
        </button>
      ))}
      <button onClick={() => setShowAdvanced(v => !v)}
        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all"
        style={{ background: showAdvanced ? ACCENT_DIM : 'rgba(255,255,255,0.04)', color: showAdvanced ? ACCENT : 'rgba(255,255,255,0.55)', border: `1px solid ${showAdvanced ? ACCENT_BORDER : 'rgba(255,255,255,0.08)'}` }}>
        <Icon name="SlidersHorizontal" size={13} />
        {showAdvanced ? 'Básico' : 'Avanzado'}
      </button>
    </div>

    {showAdvanced && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-3 border-t border-white/5">
        {['Departamento', 'Unidad de negocio', 'Canal de venta', 'Cliente', 'Proveedor', 'Comercial']?.map(f => (
          <div key={f}>
            <label className="erp-secondary-text block mb-1" style={{ fontSize: '0.6875rem' }}>{f}</label>
            <select className="w-full rounded-lg px-2.5 py-1.5 erp-filter-label"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.7)' }}>
              <option value="">Todos</option>
            </select>
          </div>
        ))}
        <div className="flex items-end gap-2 col-span-2 sm:col-span-3 lg:col-span-6">
          <button className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg erp-filter-label transition-all hover:opacity-80"
            style={{ background: ACCENT_DIM, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}>
            <Icon name="Filter" size={13} /> Aplicar filtros
          </button>
          <button className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg erp-filter-label transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="X" size={13} /> Limpiar
          </button>
          <button className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg erp-filter-label transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Bookmark" size={13} /> Guardar vista
          </button>
          <button className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg erp-filter-label transition-all hover:opacity-80 ml-auto"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Download" size={13} /> Exportar resultados
          </button>
        </div>
      </div>
    )}
  </div>
);

const ExportBar = () => (
  <div className="flex items-center gap-2 flex-wrap p-4 rounded-2xl mb-6"
    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
    <span className="erp-secondary-text mr-2">Exportar:</span>
    {[
      { label: 'PDF Ejecutivo', icon: 'FileText', color: '#F87171' },
      { label: 'KPIs Excel', icon: 'Table', color: '#34D399' },
      { label: 'Tablas CSV', icon: 'Download', color: '#60A5FA' },
      { label: 'Gráficos PNG', icon: 'Image', color: '#A78BFA' },
      { label: 'Informe Dirección', icon: 'Briefcase', color: '#FBBF24' },
    ]?.map(btn => (
      <button key={btn?.label}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg erp-secondary-text transition-all hover:opacity-80"
        style={{ background: `${btn?.color}10`, color: btn?.color, border: `1px solid ${btn?.color}25` }}>
        <Icon name={btn?.icon} size={13} />
        {btn?.label}
      </button>
    ))}
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const DashboardGeneral = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('Este mes');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [alertFilter, setAlertFilter] = useState('all');
  const [aiQuery, setAiQuery] = useState('');
  const [kpiDetailOpen, setKpiDetailOpen] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const filteredAlerts = alertFilter === 'all' ? sampleAlerts : sampleAlerts?.filter(a => a?.type === alertFilter);

  const handleAlertAction = useCallback((alert, action) => {
    // Future: connect to Supabase
  }, []);

  const handleAiQuery = useCallback((e) => {
    e?.preventDefault();
    // Future: connect to AI model
  }, []);

  return (
    <div className="min-h-screen px-4 py-6 md:px-6 lg:px-8 scrollbar-custom"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* ── PAGE HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: ACCENT_DIM, border: `1px solid ${ACCENT_BORDER}` }}>
              <Icon name="LayoutDashboard" size={22} style={{ color: ACCENT }} />
            </div>
            <div>
              <h1 className="erp-page-title">Dashboard General</h1>
              <p className="erp-page-subtitle">Las Dos Doncellas · Centro de mando empresarial</p>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-14 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.20)' }}>
              <span className="w-2 h-2 rounded-full bg-[#34D399] animate-pulse" />
              <span className="erp-secondary-text" style={{ color: '#34D399' }}>Sistema activo</span>
            </div>
            <span className="erp-secondary-text">
              {new Date()?.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <button onClick={() => setCustomizeOpen(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl erp-filter-label transition-all hover:opacity-80"
            style={{ background: ACCENT_DIM, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}>
            <Icon name="Settings2" size={15} />
            Personalizar
          </button>
          <button className="flex items-center gap-1.5 px-4 py-2 rounded-xl erp-filter-label transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.10)' }}>
            <Icon name="RefreshCw" size={15} />
            Actualizar
          </button>
        </div>
      </div>
      {/* ── FILTER BAR ── */}
      <FilterBar activeFilter={activeFilter} setActiveFilter={setActiveFilter}
        showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced} />
      {/* ── EXPORT BAR ── */}
      <ExportBar />
      {/* ── KPI ROWS ── */}
      <section className="mb-8">
        <SectionHeader icon="BarChart3" title="Resumen Ejecutivo Global" subtitle="20 indicadores clave de toda la empresa" />
        <div className="space-y-4">
          {kpiRows?.map((row, ri) => (
            <div key={ri} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {row?.map(kpi => (
                <KPICard key={kpi?.id} kpi={kpi} onClick={setKpiDetailOpen} />
              ))}
            </div>
          ))}
        </div>
      </section>
      {/* ── DEPARTMENT PERFORMANCE ── */}
      <section className="mb-8">
        <SectionHeader icon="Building2" title="Rendimiento por Departamento" subtitle="Comparativa de unidades de negocio" accent="#60A5FA" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {departments?.map(dept => (
            <DeptCard key={dept?.id} dept={dept} navigate={navigate} />
          ))}
        </div>
      </section>
      {/* ── TREASURY & LIQUIDITY ── */}
      <section className="mb-8">
        <SectionHeader icon="Wallet" title="Tesorería y Liquidez" subtitle="Posición financiera consolidada y previsiones" accent="#60A5FA" />
        <TreasuryBlock />
      </section>
      {/* ── BILLING & PROFITABILITY ── */}
      <section className="mb-8">
        <SectionHeader icon="FileText" title="Facturación y Rentabilidad" subtitle="Consolidado de facturación emitida y recibida" accent="#FBBF24" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {[
            { label: 'Facturación Emitida', icon: 'FileOutput', color: '#34D399' },
            { label: 'Facturación Recibida', icon: 'FileInput', color: '#60A5FA' },
            { label: 'Facturación Cobrada', icon: 'CheckCircle', color: '#D6EF4E' },
            { label: 'Pendiente de Cobro', icon: 'Clock', color: '#FBBF24' },
            { label: 'Facturas Vencidas', icon: 'AlertCircle', color: '#F87171' },
            { label: 'Margen Bruto', icon: 'TrendingUp', color: '#A78BFA' },
          ]?.map(item => (
            <div key={item?.label} className="rounded-xl p-4 flex items-center gap-3"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${item?.color}12`, border: `1px solid ${item?.color}25` }}>
                <Icon name={item?.icon} size={16} style={{ color: item?.color }} />
              </div>
              <div>
                <p className="erp-secondary-text">{item?.label}</p>
                <div className="erp-kpi-value text-xl" style={{ color: item?.color }}>—</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 p-3 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Icon name="Info" size={14} className="text-white/30 flex-shrink-0" />
          <p className="erp-secondary-text">{PENDING}</p>
        </div>
      </section>
      {/* ── INVENTORY ── */}
      <section className="mb-8">
        <SectionHeader icon="Package" title="Inventario Global" subtitle="Stock consolidado Tienda + Distribución (inventarios separados)" accent="#34D399" />
        <InventoryBlock />
      </section>
      {/* ── COMMERCIAL ACTIVITY ── */}
      <section className="mb-8">
        <SectionHeader icon="Users" title="Actividad Comercial" subtitle="CRM y pipeline comercial consolidado" accent="#A78BFA" />
        <CommercialBlock />
      </section>
      {/* ── SALA DE CORTE ── */}
      <section className="mb-8">
        <SectionHeader icon="Scissors" title="Sala de Corte" subtitle="Resumen operativo de producción y rendimiento" accent="#D6EF4E" />
        <SalaCorteBlock />
      </section>
      {/* ── OPERATIVE TABLES ── */}
      <section className="mb-8">
        <SectionHeader icon="Table" title="Listas Operativas" subtitle="Seguimiento de tareas y documentos pendientes" accent="#FBBF24" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OperativeTable title="Próximos Cobros" icon="ArrowDownCircle" color="#34D399"
            columns={['Cliente', 'Factura', 'Vencimiento', 'Importe', 'Estado']} rows={[]} />
          <OperativeTable title="Próximos Pagos" icon="ArrowUpCircle" color="#F87171"
            columns={['Proveedor', 'Factura', 'Vencimiento', 'Importe', 'Estado']} rows={[]} />
          <OperativeTable title="Facturas Vencidas" icon="AlertCircle" color="#FBBF24"
            columns={['Tipo', 'Entidad', 'Número', 'Vencimiento', 'Importe', 'Estado']} rows={[]} />
          <OperativeTable title="Alertas de Stock" icon="Package" color="#F97316"
            columns={['Producto', 'Inventario', 'Stock Actual', 'Mínimo', 'Estado']} rows={[]} />
          <OperativeTable title="Pedidos Pendientes" icon="ShoppingCart" color="#60A5FA"
            columns={['Pedido', 'Cliente', 'Fecha', 'Importe', 'Estado']} rows={[]} />
          <OperativeTable title="Clientes con Deuda" icon="UserX" color="#F87171"
            columns={['Cliente', 'Deuda Total', 'Vencida', 'Último Pedido', 'Estado']} rows={[]} />
        </div>
      </section>
      {/* ── ALERTS CENTER ── */}
      <section className="mb-8">
        <SectionHeader icon="Bell" title="Centro de Alertas" subtitle="Excepciones y acciones prioritarias" accent="#F87171" />
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => setAlertFilter('all')}
            className="px-3 py-1.5 rounded-lg erp-filter-label transition-all"
            style={alertFilter === 'all'
              ? { background: ACCENT_DIM, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }
              : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
            Todas ({sampleAlerts?.length})
          </button>
          {alertTypes?.map(t => (
            <button key={t?.id} onClick={() => setAlertFilter(t?.id)}
              className="px-3 py-1.5 rounded-lg erp-filter-label transition-all"
              style={alertFilter === t?.id
                ? { background: t?.bg, color: t?.color, border: `1px solid ${t?.border}` }
                : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {t?.label} ({sampleAlerts?.filter(a => a?.type === t?.id)?.length})
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filteredAlerts?.map(alert => (
            <AlertCard key={alert?.id} alert={alert} onAction={handleAlertAction} />
          ))}
        </div>
      </section>
      {/* ── AI PANEL ── */}
      <section className="mb-8">
        <SectionHeader icon="Bot" title="IA Empresarial" subtitle="Resumen inteligente y consultas en lenguaje natural" accent="#A78BFA" />
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(167,139,250,0.20)' }}>
          {/* AI Header */}
          <div className="px-5 py-4 flex items-center justify-between"
            style={{ background: 'rgba(167,139,250,0.06)', borderBottom: '1px solid rgba(167,139,250,0.12)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.30)' }}>
                <Icon name="Sparkles" size={18} style={{ color: '#A78BFA' }} />
              </div>
              <div>
                <h3 className="erp-section-title text-base">Resumen Inteligente del Día</h3>
                <p className="erp-secondary-text">Módulo de IA en preparación · Arquitectura de permisos pendiente</p>
              </div>
            </div>
            <span className="px-3 py-1 rounded-full erp-secondary-text"
              style={{ background: 'rgba(167,139,250,0.10)', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.20)' }}>
              Próximamente
            </span>
          </div>

          {/* AI Suggestions */}
          <div className="p-5">
            <p className="erp-secondary-text mb-3">Recomendaciones futuras del agente IA:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {aiSuggestions?.map((s, i) => (
                <div key={i} className="rounded-xl p-3 flex items-start gap-2.5"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <Icon name={s?.icon} size={15} style={{ color: s?.color }} className="flex-shrink-0 mt-0.5" />
                  <p className="erp-secondary-text leading-snug">{s?.text}</p>
                </div>
              ))}
            </div>

            {/* Natural Language Query */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)' }}>
              <p className="erp-kpi-label mb-3">Pregunta cualquier dato sobre tu empresa:</p>
              <form onSubmit={handleAiQuery} className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={e => setAiQuery(e?.target?.value)}
                  placeholder="Ej: ¿Cuánto necesito vender hoy para cubrir gastos?"
                  className="flex-1 rounded-xl px-4 py-3 erp-filter-label outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(167,139,250,0.25)',
                    color: 'rgba(255,255,255,0.85)',
                  }}
                />
                <button type="submit"
                  className="flex items-center gap-2 px-5 py-3 rounded-xl erp-filter-label transition-all hover:opacity-80"
                  style={{ background: 'rgba(167,139,250,0.15)', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.30)' }}>
                  <Icon name="Send" size={15} />
                  <span className="hidden sm:inline">Preguntar</span>
                </button>
              </form>
              <div className="mt-3 flex flex-wrap gap-2">
                {aiQuestions?.slice(0, 4)?.map((q, i) => (
                  <button key={i} onClick={() => setAiQuery(q)}
                    className="text-xs px-2.5 py-1 rounded-lg transition-all hover:opacity-80"
                    style={{ background: 'rgba(167,139,250,0.08)', color: 'rgba(167,139,250,0.7)', border: '1px solid rgba(167,139,250,0.15)' }}>
                    {q}
                  </button>
                ))}
              </div>
              <p className="erp-secondary-text mt-3 text-xs">
                ⚠️ La IA no está conectada todavía. Se presentará la arquitectura de permisos, seguridad y acceso a datos antes de activarla.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* ── KPI DETAIL MODAL ── */}
      {kpiDetailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => setKpiDetailOpen(null)}>
          <div className="rounded-2xl p-6 max-w-md w-full"
            style={{ background: '#161616', border: `1px solid ${kpiDetailOpen?.color}30` }}
            onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${kpiDetailOpen?.color}12`, border: `1px solid ${kpiDetailOpen?.color}25` }}>
                  <Icon name={kpiDetailOpen?.icon} size={18} style={{ color: kpiDetailOpen?.color }} />
                </div>
                <h3 className="erp-section-title">{kpiDetailOpen?.title}</h3>
              </div>
              <button onClick={() => setKpiDetailOpen(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all">
                <Icon name="X" size={16} className="text-white/60" />
              </button>
            </div>
            <div className="erp-kpi-value text-4xl mb-2" style={{ color: kpiDetailOpen?.color }}>—</div>
            <p className="erp-secondary-text mb-4">{PENDING}</p>
            <div className="grid grid-cols-2 gap-3">
              {['Periodo anterior', 'Variación', 'Objetivo', 'Cumplimiento']?.map(l => (
                <div key={l} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="erp-secondary-text mb-1">{l}</p>
                  <p className="erp-kpi-value text-lg text-white/40">—</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ── CUSTOMIZE PANEL ── */}
      {customizeOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => setCustomizeOpen(false)}>
          <div className="rounded-2xl p-6 max-w-lg w-full"
            style={{ background: '#161616', border: `1px solid ${ACCENT_BORDER}` }}
            onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="erp-section-title">Personalizar Dashboard</h3>
              <button onClick={() => setCustomizeOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all">
                <Icon name="X" size={16} className="text-white/60" />
              </button>
            </div>
            <p className="erp-secondary-text mb-4">Selecciona qué secciones mostrar y el orden de los módulos.</p>
            <div className="space-y-2 mb-5">
              {['KPIs Ejecutivos', 'Rendimiento Departamentos', 'Tesorería', 'Facturación', 'Inventario', 'Comercial', 'Sala de Corte', 'Listas Operativas', 'Alertas', 'IA Empresarial']?.map(s => (
                <label key={s} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:bg-white/5 transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                  <input type="checkbox" defaultChecked className="w-4 h-4 rounded accent-[#D6EF4E]" />
                  <span className="erp-filter-label">{s}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button className="flex-1 py-2.5 rounded-xl erp-filter-label transition-all hover:opacity-80"
                style={{ background: ACCENT_DIM, color: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}>
                Guardar configuración
              </button>
              <button onClick={() => setCustomizeOpen(false)}
                className="px-4 py-2.5 rounded-xl erp-filter-label transition-all hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.10)' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── FOOTER ── */}
      <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="erp-secondary-text">Las Dos Doncellas · ERP Empresarial · Dashboard General</p>
        <p className="erp-secondary-text">Todos los datos pendientes de conexión con Supabase</p>
      </div>
    </div>
  );
};

export default DashboardGeneral;
